import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import {
  Wrench,
  MapPin,
  User,
  Play,
  Pause,
  CheckCircle,
  History,
  Plus,
  Minus,
  RotateCcw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toTitleCase } from '@/lib/utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageHeader, TabCount, DataTable, Column } from '@/components/common';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  workOrdersApi,
  WorkOrder,
  WorkOrderLineItem,
  WORK_ORDER_STATUSES,
} from '@/api/services/work-orders.api';
import { inventoryApi, InventoryItem } from '@/api/services/inventory.api';

const priorityColors: Record<string, string> = {
  LOW: 'bg-muted text-muted-foreground',
  MEDIUM: 'bg-primary-50 text-primary-500',
  HIGH: 'bg-warning-50 text-warning-600',
  URGENT: 'bg-warning-100 text-warning-700',
  EMERGENCY: 'bg-danger-50 text-danger-500',
};

const statusColors: Record<string, string> = {
  DRAFT: 'bg-muted text-muted-foreground',
  PENDING_APPROVAL: 'bg-warning-50 text-warning-600',
  APPROVED: 'bg-success-50 text-success-600',
  SCHEDULED: 'bg-primary-50 text-primary-500',
  IN_PROGRESS: 'bg-status-info-subtle text-status-info-foreground',
  ON_HOLD: 'bg-warning-100 text-warning-700',
  COMPLETED: 'bg-success-50 text-success-600',
  CANCELLED: 'bg-danger-50 text-danger-500',
  CLOSED: 'bg-muted text-muted-foreground',
};

export default function WorkOrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [workOrder, setWorkOrder] = useState<WorkOrder | null>(null);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [isCompleteOpen, setIsCompleteOpen] = useState(false);
  const [isAddPartOpen, setIsAddPartOpen] = useState(false);
  const [isIssueOpen, setIsIssueOpen] = useState(false);
  const [isReturnOpen, setIsReturnOpen] = useState(false);
  const [selectedLineItem, setSelectedLineItem] = useState<any>(null);

  // Form states
  const [completeForm, setCompleteForm] = useState({
    completionNotes: '',
    actualHours: '',
    actualCost: '',
  });
  const [addPartForm, setAddPartForm] = useState({
    itemId: '',
    plannedQty: '',
    unit: 'EA',
  });
  const [issueQty, setIssueQty] = useState('');
  const [returnQty, setReturnQty] = useState('');
  const [returnNotes, setReturnNotes] = useState('');
  const loadWorkOrder = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await workOrdersApi.getById(id);
      setWorkOrder(response.data);
    } catch (error) {
      toast.error('Failed to load work order');
      navigate('/work-orders');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (id) {
      loadWorkOrder();
      loadInventoryItems();
    }
  }, [id, loadWorkOrder]);

  const loadInventoryItems = async () => {
    try {
      const response = await inventoryApi.getAll({ limit: 100 });
      setInventoryItems(response.data);
    } catch (error) {
      console.error('Failed to load inventory items');
    }
  };

  const handleStatusUpdate = async (newStatus: string, reason?: string) => {
    if (!workOrder) return;
    try {
      await workOrdersApi.updateStatus(workOrder.id, { status: newStatus, reason });
      toast.success('Status updated successfully');
      loadWorkOrder();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    }
  };

  const handleComplete = async () => {
    if (!workOrder) return;
    try {
      await workOrdersApi.complete(workOrder.id, {
        completionNotes: completeForm.completionNotes,
        actualHours: completeForm.actualHours ? parseFloat(completeForm.actualHours) : undefined,
        actualCost: completeForm.actualCost ? parseFloat(completeForm.actualCost) : undefined,
      });
      toast.success('Work order completed successfully');
      setIsCompleteOpen(false);
      loadWorkOrder();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to complete work order');
    }
  };

  const handleAddPart = async () => {
    if (!workOrder) return;
    try {
      await workOrdersApi.addLineItem(workOrder.id, {
        itemId: addPartForm.itemId,
        plannedQty: parseFloat(addPartForm.plannedQty),
        unit: addPartForm.unit,
      });
      toast.success('Part added successfully');
      setIsAddPartOpen(false);
      setAddPartForm({ itemId: '', plannedQty: '', unit: 'EA' });
      loadWorkOrder();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to add part');
    }
  };

  const handleIssueParts = async () => {
    if (!workOrder || !selectedLineItem) return;
    try {
      await workOrdersApi.issueParts(workOrder.id, selectedLineItem.id, parseFloat(issueQty));
      toast.success('Parts issued successfully');
      setIsIssueOpen(false);
      setIssueQty('');
      loadWorkOrder();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to issue parts');
    }
  };

  const handleReturnParts = async () => {
    if (!workOrder || !selectedLineItem) return;
    try {
      await workOrdersApi.returnParts(
        workOrder.id,
        selectedLineItem.id,
        parseFloat(returnQty),
        returnNotes
      );
      toast.success('Parts returned successfully');
      setIsReturnOpen(false);
      setReturnQty('');
      setReturnNotes('');
      loadWorkOrder();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to return parts');
    }
  };

  if (loading || !workOrder) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  const getTimeProgress = () => {
    if (!workOrder.actualStart || !workOrder.estimatedHours) return 0;
    const elapsed = (Date.now() - new Date(workOrder.actualStart).getTime()) / (1000 * 60 * 60);
    return Math.min(100, Math.round((elapsed / workOrder.estimatedHours) * 100));
  };

  const lineItemColumns: Column<WorkOrderLineItem>[] = [
    {
      key: 'item',
      header: 'Item',
      render: (item) => (
        <div>
          <div className="font-medium">{item.item?.name}</div>
          <div className="text-xs leading-4 text-muted-foreground">{item.item?.sku}</div>
        </div>
      ),
    },
    {
      key: 'plannedQty',
      header: 'Planned',
      className: 'text-right',
      render: (item) => (
        <div className="text-right">
          {item.plannedQty} {item.unit}
        </div>
      ),
    },
    {
      key: 'issuedQty',
      header: 'Issued',
      className: 'text-right',
      render: (item) => (
        <div className="text-right">
          {item.issuedQty} {item.unit}
        </div>
      ),
    },
    {
      key: 'returnedQty',
      header: 'Returned',
      className: 'text-right',
      render: (item) => (
        <div className="text-right">
          {item.returnedQty} {item.unit}
        </div>
      ),
    },
    {
      key: 'consumedQty',
      header: 'Consumed',
      className: 'text-right',
      render: (item) => (
        <div className="text-right">
          {item.consumedQty || item.issuedQty - item.returnedQty} {item.unit}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-[120px]',
      render: (item) =>
        workOrder.status === 'IN_PROGRESS' ? (
          <div className="flex justify-end gap-1">
            <Button
              aria-label="Issue parts"
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedLineItem(item);
                setIsIssueOpen(true);
              }}
              title="Issue Parts"
            >
              <Minus className="h-4 w-4" />
            </Button>
            <Button
              aria-label="Return parts"
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedLineItem(item);
                setIsReturnOpen(true);
              }}
              title="Return Parts"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      {/* Header */}
      <motion.div variants={fadeUp}>
        <PageHeader
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Work Orders', href: '/work-orders' },
            { label: workOrder.workOrderNumber },
          ]}
          title={workOrder.workOrderNumber}
          description={workOrder.title}
          titleAdornment={
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={statusColors[workOrder.status]}>
                {WORK_ORDER_STATUSES.find((s) => s.value === workOrder.status)?.label}
              </Badge>
              <Badge className={priorityColors[workOrder.priority]}>{workOrder.priority}</Badge>
            </div>
          }
          actions={
            <div className="flex gap-2">
              {workOrder.status === 'DRAFT' && (
                <Button onClick={() => handleStatusUpdate('PENDING_APPROVAL')}>
                  Submit for Approval
                </Button>
              )}
              {workOrder.status === 'PENDING_APPROVAL' && (
                <Button onClick={() => handleStatusUpdate('APPROVED')}>Approve</Button>
              )}
              {['APPROVED', 'SCHEDULED'].includes(workOrder.status) && (
                <Button onClick={() => handleStatusUpdate('IN_PROGRESS')}>
                  <Play className="mr-2 h-4 w-4" />
                  Start Work
                </Button>
              )}
              {workOrder.status === 'IN_PROGRESS' && (
                <>
                  <Button variant="outline" onClick={() => handleStatusUpdate('ON_HOLD')}>
                    <Pause className="mr-2 h-4 w-4" />
                    Hold
                  </Button>
                  <Button onClick={() => setIsCompleteOpen(true)}>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Complete
                  </Button>
                </>
              )}
              {workOrder.status === 'ON_HOLD' && (
                <Button onClick={() => handleStatusUpdate('IN_PROGRESS')}>
                  <Play className="mr-2 h-4 w-4" />
                  Resume
                </Button>
              )}
              {workOrder.status === 'COMPLETED' && (
                <Button onClick={() => handleStatusUpdate('CLOSED')}>Close Work Order</Button>
              )}
            </div>
          }
        />
      </motion.div>

      {/* Overview Cards */}
      <motion.div
        variants={fadeUp}
        className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4 mt-6"
      >
        <Card>
          <CardHeader className="pb-2">
            <h3 className="text-base font-semibold text-foreground">Equipment</h3>
          </CardHeader>
          <CardContent>
            {workOrder.equipment ? (
              <div className="flex items-center gap-2">
                <Wrench className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="font-medium">{workOrder.equipment.name}</div>
                  <div className="text-xs leading-4 text-muted-foreground">
                    {workOrder.equipment.assetNumber}
                  </div>
                </div>
              </div>
            ) : (
              <span className="text-muted-foreground">No equipment assigned</span>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <h3 className="text-base font-semibold text-foreground">Location</h3>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-muted-foreground" />
              <div>
                <div className="font-medium">{workOrder.location?.name}</div>
                <div className="text-xs leading-4 text-muted-foreground">
                  {workOrder.location?.code}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <h3 className="text-base font-semibold text-foreground">Assignee</h3>
          </CardHeader>
          <CardContent>
            {workOrder.assignedTo ? (
              <div className="flex items-center gap-2">
                <User className="h-5 w-5 text-muted-foreground" />
                <div className="font-medium">{workOrder.assignedTo.fullName}</div>
              </div>
            ) : (
              <span className="text-muted-foreground">Unassigned</span>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <h3 className="text-base font-semibold text-foreground">Time Tracking</h3>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>
                  {workOrder.actualHours || 0}h / {workOrder.estimatedHours || '-'}h
                </span>
                <span>{getTimeProgress()}%</span>
              </div>
              <Progress value={getTimeProgress()} className="h-2" />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Tabs */}
      <motion.div variants={fadeUp} className="mt-6">
        <Tabs defaultValue="details" className="space-y-4">
          <TabsList>
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="parts">
              Parts &amp; Materials
              <TabCount value={workOrder.lineItems?.length ?? null} />
            </TabsTrigger>
            <TabsTrigger value="history">
              Status History
              <TabCount value={workOrder.statusHistory?.length ?? null} />
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-4">
            <Card>
              <CardHeader>
                <h3 className="text-base font-semibold text-foreground">Work Order Details</h3>
              </CardHeader>
              <CardContent className="space-y-4">
                {workOrder.description && (
                  <div>
                    <Label className="text-muted-foreground">Description</Label>
                    <p className="mt-1 whitespace-pre-wrap">{workOrder.description}</p>
                  </div>
                )}
                <Separator />
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  <div>
                    <Label className="text-muted-foreground">Type</Label>
                    <p className="font-medium">{toTitleCase(workOrder.type)}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Requested By</Label>
                    <p className="font-medium">{workOrder.requestedBy?.fullName}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Scheduled Start</Label>
                    <p className="font-medium">
                      {workOrder.scheduledStart
                        ? format(new Date(workOrder.scheduledStart), 'MMM d, yyyy h:mm a')
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Scheduled End</Label>
                    <p className="font-medium">
                      {workOrder.scheduledEnd
                        ? format(new Date(workOrder.scheduledEnd), 'MMM d, yyyy h:mm a')
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Actual Start</Label>
                    <p className="font-medium">
                      {workOrder.actualStart
                        ? format(new Date(workOrder.actualStart), 'MMM d, yyyy h:mm a')
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Actual End</Label>
                    <p className="font-medium">
                      {workOrder.actualEnd
                        ? format(new Date(workOrder.actualEnd), 'MMM d, yyyy h:mm a')
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Est. Cost</Label>
                    <p className="font-medium">
                      {workOrder.estimatedCost ? `$${workOrder.estimatedCost.toFixed(2)}` : '-'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Actual Cost</Label>
                    <p className="font-medium">
                      {workOrder.actualCost ? `$${workOrder.actualCost.toFixed(2)}` : '-'}
                    </p>
                  </div>
                </div>
                {workOrder.completionNotes && (
                  <>
                    <Separator />
                    <div>
                      <Label className="text-muted-foreground">Completion Notes</Label>
                      <p className="mt-1 whitespace-pre-wrap">{workOrder.completionNotes}</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="parts" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <h3 className="text-base font-semibold text-foreground">Parts & Materials</h3>
                {['DRAFT', 'APPROVED', 'SCHEDULED'].includes(workOrder.status) && (
                  <Button size="sm" onClick={() => setIsAddPartOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Part
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <DataTable<WorkOrderLineItem>
                  pagination={false}
                  data={workOrder.lineItems || []}
                  columns={lineItemColumns}
                  emptyTitle="No parts added yet"
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="space-y-4">
            <Card>
              <CardHeader>
                <h3 className="text-base font-semibold text-foreground">Status History</h3>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {workOrder.statusHistory?.map((history, index) => (
                    <div key={history.id} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                          <History className="h-4 w-4 text-primary" />
                        </div>
                        {index < (workOrder.statusHistory?.length || 0) - 1 && (
                          <div className="h-full w-px bg-border" />
                        )}
                      </div>
                      <div className="flex-1 pb-4">
                        <div className="flex items-center gap-2">
                          {history.fromStatus && (
                            <>
                              <Badge variant="outline">{history.fromStatus}</Badge>
                              <span className="text-muted-foreground">→</span>
                            </>
                          )}
                          <Badge className={statusColors[history.toStatus]}>
                            {history.toStatus}
                          </Badge>
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">
                          By {history.changedBy?.fullName} •{' '}
                          {formatDistanceToNow(new Date(history.createdAt), { addSuffix: true })}
                        </p>
                        {history.reason && <p className="mt-1 text-sm">{history.reason}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </motion.div>

      {/* Complete Work Order Dialog */}
      <Dialog open={isCompleteOpen} onOpenChange={setIsCompleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Work Order</DialogTitle>
            <DialogDescription>
              Enter completion details for work order {workOrder.workOrderNumber}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Actual Hours</Label>
                <Input
                  type="number"
                  step="0.5"
                  placeholder={workOrder.estimatedHours?.toString() || '0'}
                  value={completeForm.actualHours}
                  onChange={(e) =>
                    setCompleteForm({ ...completeForm, actualHours: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Actual Cost ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder={workOrder.estimatedCost?.toString() || '0'}
                  value={completeForm.actualCost}
                  onChange={(e) => setCompleteForm({ ...completeForm, actualCost: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Completion Notes</Label>
              <Textarea
                placeholder="Summary of work performed..."
                rows={4}
                value={completeForm.completionNotes}
                onChange={(e) =>
                  setCompleteForm({ ...completeForm, completionNotes: e.target.value })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCompleteOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleComplete}>Complete Work Order</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Part Dialog */}
      <Dialog open={isAddPartOpen} onOpenChange={setIsAddPartOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Part</DialogTitle>
            <DialogDescription>
              Add parts or materials required for this work order
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Item</Label>
              <Select
                value={addPartForm.itemId}
                onValueChange={(v) => setAddPartForm({ ...addPartForm, itemId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select item" />
                </SelectTrigger>
                <SelectContent>
                  {inventoryItems.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name} ({item.sku})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={addPartForm.plannedQty}
                  onChange={(e) => setAddPartForm({ ...addPartForm, plannedQty: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Unit</Label>
                <Input
                  value={addPartForm.unit}
                  onChange={(e) => setAddPartForm({ ...addPartForm, unit: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddPartOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddPart}
              disabled={!addPartForm.itemId || !addPartForm.plannedQty}
            >
              Add Part
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issue Parts Dialog */}
      <Dialog open={isIssueOpen} onOpenChange={setIsIssueOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Issue Parts</DialogTitle>
            <DialogDescription>
              Issue {selectedLineItem?.item?.name} from inventory
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>
                Quantity to Issue (max:{' '}
                {selectedLineItem
                  ? selectedLineItem.plannedQty -
                    selectedLineItem.issuedQty +
                    selectedLineItem.returnedQty
                  : 0}{' '}
                {selectedLineItem?.unit})
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={issueQty}
                onChange={(e) => setIssueQty(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsIssueOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleIssueParts} disabled={!issueQty || parseFloat(issueQty) <= 0}>
              Issue Parts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return Parts Dialog */}
      <Dialog open={isReturnOpen} onOpenChange={setIsReturnOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Return Parts</DialogTitle>
            <DialogDescription>
              Return unused {selectedLineItem?.item?.name} to inventory
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>
                Quantity to Return (max:{' '}
                {selectedLineItem
                  ? selectedLineItem.issuedQty -
                    selectedLineItem.returnedQty -
                    selectedLineItem.consumedQty
                  : 0}{' '}
                {selectedLineItem?.unit})
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={returnQty}
                onChange={(e) => setReturnQty(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Input
                placeholder="Reason for return..."
                value={returnNotes}
                onChange={(e) => setReturnNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReturnOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleReturnParts} disabled={!returnQty || parseFloat(returnQty) <= 0}>
              Return Parts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.main>
  );
}
